import { memo, useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as MuiIcons from '@mui/icons-material';
import type { ModalsType } from 'types';
import { useTranslation } from 'react-i18next';
import { ActionModal } from 'components/mui/common/ActionModal';
import { Button } from '@mui/material';
import { Content, IconWrapper, IconsRow, IconsWrapper } from './IconSelectModal.styles';
import { SearchField } from 'components/common/Table/actions';
import { VirtualPanel } from 'components/mui/common/VirtualPanel';

export interface Props extends ModalsType {
  onConfirm: (iconName: string) => void;
  onCancel?: () => void;
  dataTestIdPrefix?: string;
}

interface IconsRowComponentProps {
  row: string[];
  onClick: (iconName: string) => void;
}

const IconsRowComponent = memo(({ row, onClick }: IconsRowComponentProps) => (
  <IconsRow>
    {row.map((iconName) => {
      const IconComponent = MuiIcons[iconName as keyof typeof MuiIcons];
      return (
        <IconWrapper key={iconName} data-testid={`icon-select-option-${iconName}`} onClick={() => onClick(iconName)}>
          <IconComponent />
        </IconWrapper>
      );
    })}
  </IconsRow>
));

export const IconSelectModal = memo(({ open, onClose, onConfirm, onCancel, dataTestIdPrefix }: Props) => {
  const { t } = useTranslation();

  const { control, watch } = useForm({
    defaultValues: {
      search: '',
    },
  });

  const search = watch('search');

  const iconNames = useMemo(() => {
    const excludedNames = ['sharp', 'twotone', 'rounded', 'outlined'];

    return Object.keys(MuiIcons).filter(
      (iconName: string) =>
        iconName.toLowerCase().includes(search?.toLowerCase()) && !Array.from(excludedNames).some((name) => iconName.toLowerCase().includes(name))
    );
  }, [search]);

  const handleClick = useCallback(
    (iconName: string) => {
      onConfirm(iconName);
      onClose();
    },
    [onConfirm, onClose]
  );

  const splitArray = (arr: string[], chunkSize: number) => {
    const result = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
      result.push(arr.slice(i, i + chunkSize));
    }
    return result;
  };

  const iconChunks = useMemo(() => splitArray(iconNames, 6), [iconNames]);

  return (
    <ActionModal
      dataTestIds={{
        dialog: dataTestIdPrefix ? `${dataTestIdPrefix}-modal` : undefined,
        cancelButton: dataTestIdPrefix ? `${dataTestIdPrefix}-cancel-button` : undefined,
      }}
      maxWidth="xs"
      open={open}
      onClose={onClose}
      title={t('common.icon')}
      onCancel={onCancel}>
      <Content>
        <Controller
          control={control}
          name="search"
          render={({ field, fieldState: { error } }) => (
            <SearchField
              placeholder={t('common.search.icons')}
              dataTestId={dataTestIdPrefix ? `${dataTestIdPrefix}-search-input` : undefined}
              {...field}
              error={Boolean(error)}
              helperText={error?.message}
              fullWidth
              background="white"
            />
          )}
        />
        <IconsWrapper>
          <VirtualPanel<string[]> items={iconChunks} Component={IconsRowComponent} onClick={handleClick} />
        </IconsWrapper>
        <Button
          variant="text"
          onClick={() => handleClick('')}
          data-testid={dataTestIdPrefix ? `${dataTestIdPrefix}-remove-button` : 'remove-icon-btn'}>
          {t('common.removeIcon')}
        </Button>
      </Content>
    </ActionModal>
  );
});
